import styles from "./TransparentDrawer.module.scss";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import PayYourBill from "./layouts/PayYourBill";
import PayOrSplitBill from "./layouts/PayOrSplitBill";
import SplitTheBill from "./layouts/SplitTheBill";
import SelectedSplitTheBill from "./layouts/SelectedSplitTheBill";
import Grid from "../../config/Grid/Grid";
import ExtraTip from "./layouts/ExtraTip";
import ConfirmAndPay from "./layouts/ConfirmAndPay/ConfirmAndPay";
import LeaveTip from "./layouts/LeaveTip";
import CheckoutFee from "./layouts/CheckoutFee";
import PaymentError from "./layouts/PaymentError";
import Feedback from "./layouts/Feedback";
import ReceiptSent from "./layouts/ReceiptSent";
import GetReceipt from "./layouts/GetReceipt";
import SuccessCompletePayment from "./layouts/SuccessCompletePayment";
import Summary from "./layouts/Summary";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";

function TransparentDrawer() {
  const location = useLocation();
  const { remainingAmount } = useVenuesDataStore();

  const [drawerContent, setDrawerContent] = useState<JSX.Element | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastPathElement = location.pathname.split("/").pop();
  useEffect(() => {
    switch (lastPathElement) {
      case "success-complete-payment":
        setDrawerContent(<SuccessCompletePayment />);
        break;
      case "summary":
        setDrawerContent(<Summary />);
        break;
      case "get-receipt":
        setDrawerContent(<GetReceipt />);
        break;
      case "receipt-sent":
        setDrawerContent(<ReceiptSent />);
        break;
      case "feedback":
        setDrawerContent(<Feedback />);
        break;
      case "payment-error":
        setDrawerContent(<PaymentError />);
        break;
      case "checkout-fee":
        setDrawerContent(<CheckoutFee />);
        break;
      case "leave-tip":
        setDrawerContent(<LeaveTip />);
        break;
      case "confirm-and-pay":
        setDrawerContent(<ConfirmAndPay />);
        break;
      case "extra-tip":
        setDrawerContent(<ExtraTip />);
        break;
      case "pay-for-your-items":
      case "custom":
      case "equally":
        setDrawerContent(<SelectedSplitTheBill />);
        break;
      case "pay-your-bill":
        setDrawerContent(<PayYourBill />);
        break;
      case "split-the-bill":
        setDrawerContent(<SplitTheBill />);
        break;
      case "bills":
        setDrawerContent(<PayOrSplitBill />);
        break;
      default:
        setDrawerContent(null);
        break;
    }
  }, [lastPathElement]);

  const isHidden =
    !drawerContent || (remainingAmount === 0 && lastPathElement === "bills");

  // Publish the drawer's height so pages can pad their content by it and scroll
  // clear of this fixed overlay. The height differs per layout (and grows when
  // text wraps), so it is measured rather than hardcoded.
  useEffect(() => {
    const root = document.documentElement;
    const node = wrapperRef.current;

    if (isHidden || !node) {
      root.style.setProperty("--transparent-drawer-height", "0px");
      return;
    }

    const publishHeight = () => {
      root.style.setProperty(
        "--transparent-drawer-height",
        `${node.offsetHeight}px`,
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
      root.style.setProperty("--transparent-drawer-height", "0px");
    };
  }, [isHidden, drawerContent]);

  if (isHidden) {
    return null;
  }

  return (
    <div ref={wrapperRef} className={styles.transparentDrawerWrapper}>
      <Grid>
        <div className={`start-1 col-4 ${styles.transparentDrawerContainer}`}>
          {drawerContent}
        </div>
      </Grid>
    </div>
  );
}

export default TransparentDrawer;
